/**
 * Profile Page
 */

import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useProfile,
  useUpdateProfile,
  useChangePassword,
} from "@/hooks/useAuth";
import { useAuthStore } from "@/store/authStore";
import { getApiErrorMessage } from "@/utils/errors";
import { passwordSchema as newPasswordSchema } from "@/utils/schemas";
import { PASSWORD_RULES_SUMMARY, passwordFieldDescribedBy } from "@/utils/passwordRules";
import { PasswordRequirements } from "@/components/common/PasswordRequirements";
import { Link as RouterLink } from "react-router-dom";
import { NotificationsOutlined as NotificationsIcon } from "@mui/icons-material";
import { NOTIFICATION_SETTINGS_ROLES } from "@/pages/settings/notificationSettingsRows";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const profileSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: z.string().optional(),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Current password is required"),
    new_password: newPasswordSchema,
    confirm_password: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  });

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export const ProfilePage: React.FC = () => {
  const [tabValue, setTabValue] = React.useState(0);
  const { user } = useAuthStore();
  const hasNotificationSettings = (
    NOTIFICATION_SETTINGS_ROLES as readonly string[]
  ).includes(user?.role ?? "");
  const { data: profile, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  // The error shows inline above the form, so no toast as well.
  const changePasswordMutation = useChangePassword({ toastErrors: false });

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
    },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch: watchPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const newPasswordValue = watchPassword("new_password");

  React.useEffect(() => {
    if (profile) {
      resetProfile({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || "",
      });
    }
  }, [profile, resetProfile]);

  const onProfileSubmit = async (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    changePasswordMutation.mutate(
      {
        current_password: data.current_password,
        new_password: data.new_password,
      },
      {
        onSuccess: () => {
          resetPassword();
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{
        fontWeight: 700
      }}>
        My Profile
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4
        }}>
        Manage your profile information and security settings
      </Typography>
      {/* Profile Card with Avatar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: "primary.main",
                fontSize: 40,
                fontWeight: 700,
              }}
            >
              {user?.first_name?.[0]}
              {user?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{
                fontWeight: 600
              }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body1" sx={{
                color: "text.secondary"
              }}>
                {user?.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "inline-block",
                  mt: 1,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "primary.light",
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                {user?.role.replace("_", " ").toUpperCase()}
              </Typography>
            </Box>
          </Box>
          {hasNotificationSettings && (
            <Button
              component={RouterLink}
              to="/settings/notifications"
              variant="outlined"
              startIcon={<NotificationsIcon />}
              sx={{ mt: 3 }}
            >
              Notification settings
            </Button>
          )}
        </CardContent>
      </Card>
      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
          >
            <Tab label="Profile Information" />
            <Tab label="Security" />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <CardContent sx={{ px: 4 }}>
            {updateProfileMutation.isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {getApiErrorMessage(updateProfileMutation.error, "Failed to update profile")}
              </Alert>
            )}

            <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="first_name"
                    control={profileControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name"
                        fullWidth
                        error={!!profileErrors.first_name}
                        helperText={profileErrors.first_name?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="last_name"
                    control={profileControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        fullWidth
                        error={!!profileErrors.last_name}
                        helperText={profileErrors.last_name?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <TextField
                    label="Email Address"
                    value={user?.email}
                    fullWidth
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="phone"
                    control={profileControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number"
                        fullWidth
                        error={!!profileErrors.phone}
                        helperText={profileErrors.phone?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </form>
          </CardContent>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={1}>
          <CardContent sx={{ px: 4 }}>
            <Typography variant="h6" gutterBottom sx={{
              fontWeight: 600
            }}>
              Change Password
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 3
              }}>
              {PASSWORD_RULES_SUMMARY}. Changing it signs you out on your
              other devices.
            </Typography>

            {changePasswordMutation.isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {getApiErrorMessage(changePasswordMutation.error, "Failed to change password")}
              </Alert>
            )}

            {changePasswordMutation.isSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Password changed successfully!
              </Alert>
            )}

            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Controller
                    name="current_password"
                    control={passwordControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Current Password"
                        type="password"
                        fullWidth
                        error={!!passwordErrors.current_password}
                        helperText={passwordErrors.current_password?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="new_password"
                    control={passwordControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="New Password"
                        id="profile-new-password"
                        type="password"
                        fullWidth
                        error={!!passwordErrors.new_password}
                        helperText={passwordErrors.new_password?.message}
                        required
                        slotProps={{
                          htmlInput: {
                            "aria-describedby": passwordFieldDescribedBy(
                              "profile-new-password",
                              "profile-password-requirements",
                              !!passwordErrors.new_password,
                            ),
                          },
                        }}
                      />
                    )}
                  />
                  <PasswordRequirements
                    id="profile-password-requirements"
                    password={newPasswordValue ?? ""}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name="confirm_password"
                    control={passwordControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        error={!!passwordErrors.confirm_password}
                        helperText={passwordErrors.confirm_password?.message}
                        required
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Change Password"
                )}
              </Button>
            </form>
          </CardContent>
        </TabPanel>
      </Card>
    </Box>
  );
};
